import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  styled,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  CircularProgress,
  SelectChangeEvent,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getStates, StateData } from "@services/location";
import { getCurrentUserId, getCurrentUser } from "@utils/userUtils";
import { createUserPreferredFacility } from "@services/payment";
import {
  FAA_NASR_AIRPORTS_LOOKUP_SOURCE,
  FDA_ESTABLISHMENT_LOOKUP_SOURCE,
  lookupFacilitiesByVertical,
  type VerticalFacilityResult,
} from "@services/verticalFacilitySearch";
import {
  getFacilityTypesByVertical,
  type FacilityTypeByVerticalRow,
} from "@services/facilityTypesByVertical";
import { ActivityFamilyLabelWithInfo } from "@components/Aviation/ActivityFamilyLabelWithInfo";
import { WizardOptionWithBlurb } from "@components/Aviation/WizardOptionWithBlurb";
import { useAviationWizardConfig } from "@hooks/useAviationWizardConfig";
import { resolveAviationTargeting } from "@services/aviationTaxonomy";
import {
  AVIATION_LOCATION_FIELD_BLURBS,
  DEFAULT_AVIATION_FOUR_STEP,
  buildAviationTargetingPayload,
  effectivePrimaryActivityFamilyForWizard,
  getAuthorityProfileStepDescription,
  getWizardOptionBlurb,
  hasAviationStep1Selection,
  selectedProfileSlugs,
  toggleProfileSlugSelection,
  type AviationFourStepWizardState,
  type PrimaryActivityFamily,
} from "@utils/aviationWizardConfig";

const ContentContainer = styled(Box)({
  backgroundColor: "#fdffff",
  minHeight: "98vh",
  borderRadius: "12px",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
  padding: "32px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  boxSizing: "border-box",
});

const SectionTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: "18px",
  color: "#0d3b66",
});

const FormRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "16px",
});

const SearchButton = styled(Button)({
  alignSelf: "flex-start",
  textTransform: "none",
  backgroundColor: "#0d3b66",
  color: "#fff",
  ":hover": { backgroundColor: "#0b2f50" },
});

const ResultCard = styled(Box)({
  border: "1px solid #99d4e1",
  borderRadius: "8px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  backgroundColor: "#f9fdff",
  cursor: "pointer",
  transition: "border-color 120ms ease",
  ":hover": { borderColor: "#0d3b66" },
});

const NoResultsBanner = styled(Box)({
  border: "1px dashed #b7c9d6",
  borderRadius: "8px",
  padding: "20px",
  backgroundColor: "#fafcff",
  color: "#34495e",
});

export interface NonHealthcareFacilityScreenProps {
  /** Vertical the user picked in VerticalPreviewSwitcher (e.g. "food_service"). */
  preselectedVertical: string;
  /** Optional callback the host route can use to refresh facility lists. */
  onAfterCreate?: () => void;
}

interface SearchFormState {
  name: string;
  state: string;
  city: string;
  phone: string;
  email: string;
  facilityTypeId: number | "";
}

const EMPTY_FORM: SearchFormState = {
  name: "",
  state: "",
  city: "",
  phone: "",
  email: "",
  facilityTypeId: "",
};

type AviationTargeting = AviationFourStepWizardState;

interface FoodServiceTargeting {
  primary_profile_slug: string;
  food_service_facility_subtype: string;
  food_service_contexts: string[];
  food_service_special_processes: string[];
  regulation_packs: string[];
  overlay_packs: string[];
  penalty_packs: string[];
}

const DEFAULT_AVIATION_TARGETING = DEFAULT_AVIATION_FOUR_STEP;

const DEFAULT_FOOD_SERVICE_TARGETING: FoodServiceTargeting = {
  primary_profile_slug: "",
  food_service_facility_subtype: "",
  food_service_contexts: [],
  food_service_special_processes: [],
  regulation_packs: [
    "FDA_FOOD_CODE",
    "STATE_RETAIL_FOOD_CODE",
    "LOCAL_HEALTH_DEPARTMENT",
  ],
  overlay_packs: ["OSHA_WORKPLACE_SAFETY", "FIRE_CODE", "WATER_WASTEWATER"],
  penalty_packs: [
    "PERMIT_ENFORCEMENT",
    "INSPECTION_VIOLATIONS",
    "IMMINENT_HEALTH_HAZARD",
  ],
};

const FOOD_SERVICE_ALCOHOL_ONLY_SUBTYPES = new Set([
  "food_service_winery_tasting_room",
  "food_service_brewery_no_food",
  "food_service_cidery_tasting_room",
]);

const FOOD_SERVICE_CONTEXT_OPTIONS = [
  ["mobile", "Mobile unit"],
  ["temporary_event", "Temporary event"],
  ["commissary_required", "Commissary required"],
  ["correctional", "Correctional / detention"],
  ["school", "School / childcare"],
  ["healthcare", "Healthcare setting"],
  ["marine_vessel", "Marine vessel"],
  ["alcohol_service", "Alcohol service"],
  ["winery_tasting_room", "Winery tasting room"],
  ["brewery_taproom_no_food", "Brewery / taproom without food service"],
  ["cidery_tasting_room", "Cidery tasting room"],
  ["delivery_catering", "Delivery / catering"],
] as const;

const FOOD_SERVICE_SPECIAL_PROCESS_OPTIONS = [
  ["haccp_variance", "HACCP / variance required"],
  ["reduced_oxygen_packaging", "Reduced oxygen packaging"],
  ["sous_vide", "Sous vide"],
  ["acidification", "Acidification"],
  ["curing_smoking", "Curing / smoking for preservation"],
  ["shellfish", "Shellfish handling"],
  ["raw_animal_food", "Raw animal food service"],
] as const;

/**
 * NonHealthcareFacilityScreen
 * --------------------------------------------------------------------------
 * Sibling of FacilityValidationScreen used when the user has selected a
 * non-healthcare preview vertical (food_service, aviation_*, contracts, ...).
 *
 * Differences vs FacilityValidationScreen (deliberate):
 *   1. Search backend is /api/v1/facility-management/lookup-by-vertical
 *      (aviation/food/pharma use live public directories; other verticals
 *      search the local Facility table), NOT the NPI registry.
 *   2. Facility-type dropdown is scoped to the active vertical via
 *      /api/v1/facility-types/by-vertical.
 *   3. ALL healthcare-only validation (NPI verification, taxonomy routing,
 *      Medicare prefill, CCN entry, samhsa lookup) is intentionally absent.
 *   4. createUserPreferredFacility is called with
 *      facility_details.vertical=<slug> so the server stamps Facility.vertical
 *      and the new row is visible in the vertical-filtered Selected-Facility
 *      dropdown immediately on return to the dashboard.
 */
export default function NonHealthcareFacilityScreen({
  preselectedVertical,
  onAfterCreate,
}: NonHealthcareFacilityScreenProps): JSX.Element {
  const navigate = useNavigate();
  const isAviation = preselectedVertical === "aviation";
  const isFoodService = preselectedVertical === "food_service";
  const isPharma = preselectedVertical === "pharma_manufacturing";

  const [form, setForm] = useState<SearchFormState>(EMPTY_FORM);
  const [aviationTargeting, setAviationTargeting] = useState<AviationTargeting>(
    DEFAULT_AVIATION_TARGETING,
  );
  const [foodServiceTargeting, setFoodServiceTargeting] =
    useState<FoodServiceTargeting>(DEFAULT_FOOD_SERVICE_TARGETING);
  const [aviationWizardStep, setAviationWizardStep] = useState(0);
  const [aviationWizardComplete, setAviationWizardComplete] = useState(false);
  const [states, setStates] = useState<StateData[]>([]);
  const [facilityTypes, setFacilityTypes] = useState<FacilityTypeByVerticalRow[]>([]);
  const [results, setResults] = useState<VerticalFacilityResult[] | null>(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchNotice, setSearchNotice] = useState<string | null>(null);
  const [searchBackend, setSearchBackend] = useState<string | null>(null);
  const [aviationSuggestions, setAviationSuggestions] = useState<VerticalFacilityResult[]>([]);
  const [loadingAviationSuggestions, setLoadingAviationSuggestions] = useState(false);
  const [aviationSuggestionOpen, setAviationSuggestionOpen] = useState(false);
  const aviationSuggestionListRef = useRef<HTMLUListElement | null>(null);
  /** Invalidates in-flight lookup-by-vertical responses when deps change (typing, wizard step). */
  const aviationLookupGenerationRef = useRef(0);

  const verticalLabel = useMemo(
    () =>
      preselectedVertical
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    [preselectedVertical],
  );

  const {
    config: aviationWizardConfig,
    loading: aviationTaxonomyLoading,
    error: aviationTaxonomyError,
  } = useAviationWizardConfig(isAviation);

  const currentAviationWizardStep =
    aviationWizardConfig.wizardSteps[aviationWizardStep];
  const isFinalAviationWizardStep =
    aviationWizardStep === aviationWizardConfig.wizardSteps.length - 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getStates();
        if (!cancelled) setStates(data);
      } catch {
        // Non-fatal: state dropdown is optional.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isAviation) {
      setFacilityTypes([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await getFacilityTypesByVertical(preselectedVertical);
        if (!cancelled) setFacilityTypes(data.facility_types);
      } catch {
        // Non-fatal: form still usable without the dropdown.
        if (!cancelled) setFacilityTypes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAviation, preselectedVertical]);

  const handleField =
    (field: keyof SearchFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleAviationNameInput = (value: string) => {
    setForm((prev) => ({ ...prev, name: value }));
    setSearchError(null);
    setSearchNotice(null);
    if (!value.trim()) {
      setAviationSuggestions([]);
      setResults(null);
    }
  };

  const handleStateChange = (e: SelectChangeEvent<string>) => {
    setForm((prev) => ({ ...prev, state: e.target.value }));
  };

  const handleFacilityTypeChange = (e: SelectChangeEvent<number | "">) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      facilityTypeId: value === "" ? "" : Number(value),
    }));
  };

  const toggleAviationProfileSlug = (slug: string) => {
    setAviationTargeting((prev) => toggleProfileSlugSelection(prev, slug));
    setForm((prev) => ({
      ...prev,
      facilityTypeId: "",
    }));
    setAviationWizardComplete(false);
  };

  const setPrimaryActivityFamily = (value: PrimaryActivityFamily | "") => {
    setAviationTargeting((prev) => {
      const nextSlug =
        value && aviationWizardConfig.defaultProfileSlugByFamily[value as PrimaryActivityFamily]
          ? aviationWizardConfig.defaultProfileSlugByFamily[value as PrimaryActivityFamily]
          : "";
      return {
        ...prev,
        primary_activity_family: value as PrimaryActivityFamily | "",
        additional_activity_families: prev.additional_activity_families.filter((x) => x !== value),
        primary_profile_slug: nextSlug || prev.primary_profile_slug,
        additional_profile_slugs: [],
      };
    });
    setAviationWizardComplete(false);
  };

  const toggleAdditionalActivityFamily = (value: PrimaryActivityFamily) => {
    // Primary is excluded from the additional checkbox list in the UI, so value
    // never equals primary_activity_family when this handler runs.
    setAviationTargeting((prev) => {
      const has = prev.additional_activity_families.includes(value);
      return {
        ...prev,
        additional_activity_families: has
          ? prev.additional_activity_families.filter((x) => x !== value)
          : [...prev.additional_activity_families, value],
      };
    });
    setAviationWizardComplete(false);
  };

  type AviationToggleArrayKey =
    | "operation_contexts"
    | "asset_types"
    | "certificates_or_approvals"
    | "risk_targets"
    | "activity_domains"
    | "regulation_packs"
    | "overlay_packs"
    | "penalty_packs"
    | "legal_roles";

  const toggleAviationTargetingValue = (field: AviationToggleArrayKey, value: string) => {
    setAviationTargeting((prev) => {
      const current = prev[field];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
    setAviationWizardComplete(false);
  };

  const requireAviationStep1Selection = () => {
    if (!isAviation) return true;
    if (hasAviationStep1Selection(aviationTargeting.primary_activity_family, aviationTargeting.additional_activity_families)) {
      return true;
    }
    toast.error("Choose the main regulated aviation entity.");
    return false;
  };

  const requireAviationProfile = () => {
    if (!isAviation || aviationTargeting.primary_profile_slug) return true;
    toast.error("Select at least one authority / regulatory identity that applies.");
    return false;
  };

  const setFoodServiceSubtype = (value: string) => {
    setFoodServiceTargeting((prev) => ({
      ...prev,
      primary_profile_slug: value,
      food_service_facility_subtype: value,
      food_service_contexts: FOOD_SERVICE_ALCOHOL_ONLY_SUBTYPES.has(value)
        ? Array.from(new Set([...prev.food_service_contexts, "alcohol_service"]))
        : prev.food_service_contexts,
      regulation_packs: FOOD_SERVICE_ALCOHOL_ONLY_SUBTYPES.has(value)
        ? Array.from(
            new Set([
              ...prev.regulation_packs,
              "TTB_ALCOHOL",
              "STATE_ALCOHOL_BEVERAGE_CONTROL",
              "LOCAL_ALCOHOL_PERMIT",
            ]),
          )
        : prev.regulation_packs,
      overlay_packs: FOOD_SERVICE_ALCOHOL_ONLY_SUBTYPES.has(value)
        ? Array.from(new Set([...prev.overlay_packs, "ALCOHOL_SERVICE", "LOCAL_ZONING_LAND_USE"]))
        : prev.overlay_packs,
      penalty_packs: FOOD_SERVICE_ALCOHOL_ONLY_SUBTYPES.has(value)
        ? Array.from(
            new Set([
              ...prev.penalty_packs,
              "ALCOHOL_LICENSE_ENFORCEMENT",
              "UNDERAGE_SERVICE",
              "EXCISE_TAX_RECORDKEEPING",
            ]),
          )
        : prev.penalty_packs,
    }));
    const matchingType = facilityTypes.find((t) => t.type_slug === value);
    if (matchingType?.id) {
      setForm((prev) => ({ ...prev, facilityTypeId: Number(matchingType.id) }));
    }
  };

  const toggleFoodServiceTargetingValue = (
    field: "food_service_contexts" | "food_service_special_processes",
    value: string,
  ) => {
    setFoodServiceTargeting((prev) => {
      const current = prev[field];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  useEffect(() => {
    if (
      !isFoodService ||
      foodServiceTargeting.food_service_facility_subtype ||
      facilityTypes.length === 0
    ) {
      return;
    }
    const firstType = facilityTypes.find((type) => Boolean(type.type_slug));
    if (firstType?.type_slug) {
      setFoodServiceSubtype(firstType.type_slug);
    }
  }, [facilityTypes, foodServiceTargeting.food_service_facility_subtype, isFoodService]);

  const aviationLookupActive =
    aviationWizardComplete ||
    aviationWizardConfig.wizardSteps[aviationWizardStep]?.id === "facility_location";

  useEffect(() => {
    if (!isAviation || !aviationLookupActive) {
      aviationLookupGenerationRef.current += 1;
      setAviationSuggestions([]);
      setLoadingAviationSuggestions(false);
      return;
    }

    const trimmedName = form.name.trim();
    if (!trimmedName) {
      aviationLookupGenerationRef.current += 1;
      setAviationSuggestions([]);
      setLoadingAviationSuggestions(false);
      return;
    }

    const generation = (aviationLookupGenerationRef.current += 1);
    setLoadingAviationSuggestions(true);
    const timeout = window.setTimeout(async () => {
      try {
        const normalizedAviationTargeting = {
          ...aviationTargeting,
          ...buildAviationTargetingPayload(aviationTargeting),
        } as AviationTargeting;
        const response = await lookupFacilitiesByVertical({
          vertical: preselectedVertical,
          name: trimmedName,
          state: form.state || undefined,
          city: form.city || undefined,
          primary_profile_slug:
            normalizedAviationTargeting.primary_profile_slug || undefined,
          activity_domains:
            normalizedAviationTargeting.activity_domains.length > 0
              ? normalizedAviationTargeting.activity_domains
              : undefined,
          lookup_source: FAA_NASR_AIRPORTS_LOOKUP_SOURCE,
        });
        if (generation !== aviationLookupGenerationRef.current) return;
        const apiResults = response.success ? response.data?.results ?? [] : [];
        setAviationSuggestions(mergeAviationSuggestions(trimmedName, apiResults));
        setAviationSuggestionOpen(true);
      } catch {
        if (generation === aviationLookupGenerationRef.current) {
          setAviationSuggestions(mergeAviationSuggestions(trimmedName, []));
        }
      } finally {
        if (generation === aviationLookupGenerationRef.current) {
          setLoadingAviationSuggestions(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      aviationLookupGenerationRef.current += 1;
    };
  }, [
    aviationLookupActive,
    aviationTargeting,
    aviationWizardComplete,
    aviationWizardStep,
    form.city,
    form.name,
    form.state,
    isAviation,
    preselectedVertical,
  ]);

  useEffect(() => {
    if (aviationSuggestionListRef.current) {
      aviationSuggestionListRef.current.scrollTop = 0;
    }
  }, [aviationSuggestions, form.name]);

  const requireAviationWizardComplete = () => {
    if (!isAviation) return true;
    if (!aviationWizardComplete) {
      toast.error("Please complete the aviation wizard before continuing.");
      return false;
    }
    if (!requireAviationStep1Selection()) return false;
    if (!requireAviationProfile()) return false;
    return true;
  };

  const handleAviationWizardBack = () => {
    setAviationWizardStep((prev) => Math.max(0, prev - 1));
  };

  const handleAviationWizardNext = () => {
    const sid = currentAviationWizardStep.id;
    if (sid === "activity_family" && !requireAviationStep1Selection()) return;
    if (sid === "authority_profile" && !requireAviationProfile()) return;
    if (isFinalAviationWizardStep) {
      if (sid === "facility_location" && !form.name.trim()) {
        toast.error("Enter a facility or entity name to continue.");
        return;
      }
      setAviationWizardComplete(true);
      return;
    }
    if (sid === "activity_family") {
      setAviationTargeting((prev) => {
        const fam = effectivePrimaryActivityFamilyForWizard(
          prev.primary_activity_family,
          prev.additional_activity_families,
        );
        const slug =
          prev.primary_profile_slug ||
          aviationWizardConfig.defaultProfileSlugByFamily[fam];
        return { ...prev, primary_profile_slug: slug };
      });
    }
    setAviationWizardStep((prev) =>
      Math.min(aviationWizardConfig.wizardSteps.length - 1, prev + 1),
    );
  };

  const handleAviationWizardEdit = () => {
    setAviationWizardComplete(false);
    setAviationWizardStep(0);
  };

  const handleSearch = async () => {
    if (!requireAviationWizardComplete()) return;
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      toast.error("Please enter a facility name to search.");
      return;
    }
    setLoadingSearch(true);
    setSearchError(null);
    setSearchNotice(null);
    setSearchBackend(null);
    setResults(null);
    try {
      const normalizedAviationTargeting = {
        ...aviationTargeting,
        ...buildAviationTargetingPayload(aviationTargeting),
      } as AviationTargeting;
      const basePayload = {
        vertical: preselectedVertical,
        name: trimmedName,
        state: form.state || undefined,
        city: form.city || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        ...(isAviation
          ? {
              primary_profile_slug:
                normalizedAviationTargeting.primary_profile_slug || undefined,
              activity_domains:
                normalizedAviationTargeting.activity_domains.length > 0
                  ? normalizedAviationTargeting.activity_domains
                  : undefined,
              lookup_source: FAA_NASR_AIRPORTS_LOOKUP_SOURCE,
            }
          : {}),
        ...(isFoodService
          ? {
              primary_profile_slug:
                foodServiceTargeting.primary_profile_slug || undefined,
              profile_targeting_json: { ...foodServiceTargeting },
            }
          : {}),
        ...(isPharma
          ? {
              lookup_source: FDA_ESTABLISHMENT_LOOKUP_SOURCE,
            }
          : {}),
      };
      let response = await lookupFacilitiesByVertical(basePayload);
      if (
        isAviation &&
        response.success &&
        (response.data?.results ?? []).length === 0 &&
        form.city.trim()
      ) {
        response = await lookupFacilitiesByVertical({
          ...basePayload,
          city: undefined,
        });
        setSearchNotice(
          `No aviation match was found in ${form.city.trim()}, so we retried the search across ${form.state || "the selected state"}.`,
        );
      }
      if (!response.success) {
        // Server-side soft-failure (e.g. healthcare 409). Surface and stop.
        setSearchError(
          response.message || "Facility search failed. Please try again.",
        );
        setResults([]);
        return;
      }
      const nextResults = response.data?.results ?? [];
      setSearchBackend(response.data?.search_backend ?? null);
      if (nextResults.length > 0) {
        setSearchNotice(null);
      }
      setResults(nextResults);
    } catch (e) {
      setSearchError(
        e instanceof Error ? e.message : "Facility search failed.",
      );
      setResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const buildFacilityDetails = (
    chosen?: VerticalFacilityResult,
    resolvedAviationPayload?: Record<string, unknown> | null,
  ) => {
    const userId = getCurrentUserId();
    const user = getCurrentUser();
    const username =
      user?.email || (user as { username?: string } | null)?.username || "";
    const canSalesBypass = user?.can_sales_bypass === true;

    const normalizedAviationTargeting = {
      ...aviationTargeting,
      ...(resolvedAviationPayload ?? buildAviationTargetingPayload(aviationTargeting)),
    } as AviationTargeting;
    const selectedTypeRow = isAviation
      ? null
      : (chosen?.facility_type ?? null) ||
        facilityTypes.find(
          (t) => Number(t.id) === Number(form.facilityTypeId),
        ) ||
        null;

    const primaryAddress = chosen?.primary_address ?? null;
    const resolverTargeting =
      (chosen?.profile_targeting_json ??
        chosen?.facility?.profile_targeting_json ??
        {}) as Record<string, unknown>;
    const externalSource = chosen?.source ?? resolverTargeting.external_source;
    const externalRecordId =
      chosen?.external_id ?? resolverTargeting.external_record_id;
    const certificateOrLocationId =
      chosen?.certificate_or_location_id ??
      resolverTargeting.certificate_or_location_id;
    const sourceUrl = chosen?.source_url ?? resolverTargeting.source_url;
    const externalDisplayName =
      chosen?.site_name ||
      chosen?.organization_name ||
      resolverTargeting.external_display_name;
    const mergedAviationTargeting =
      isAviation && chosen
        ? {
            ...normalizedAviationTargeting,
            external_source: externalSource,
            external_record_id: externalRecordId,
            external_display_name: externalDisplayName,
            source_url: sourceUrl,
            certificate_or_location_id: certificateOrLocationId,
          }
        : normalizedAviationTargeting;

    return {
      facility_details: {
        user_id: String(userId),
        // No NPI/CCN for non-healthcare; mark as skipped so the server does
        // not attempt healthcare-only validation paths.
        ccn_number: undefined,
        facility_name:
          chosen?.site_name ||
          chosen?.organization_name ||
          form.name.trim() ||
          undefined,
        ...(isAviation
          ? {}
          : {
              facility_type:
                selectedTypeRow?.display_name ||
                (chosen?.facility_type?.display_name ?? undefined),
              facility_type_id:
                selectedTypeRow?.id ??
                (typeof form.facilityTypeId === "number"
                  ? form.facilityTypeId
                  : undefined),
            }),
        city:
          (primaryAddress?.city ?? undefined) ||
          form.city.trim() ||
          undefined,
        state:
          (primaryAddress?.state ?? undefined) ||
          (form.state ? form.state.toUpperCase() : undefined),
        country: primaryAddress?.country_code ?? "USA",
        address_1: primaryAddress?.address_1 ?? undefined,
        address_2: primaryAddress?.address_2 ?? undefined,
        postal_code: primaryAddress?.postal_code ?? undefined,
        phone_number:
          (primaryAddress?.telephone_number ?? undefined) ||
          form.phone.trim() ||
          undefined,
        skip_npi_validation: true,
        username,
        pricing_tier_id: 0,
        bypass_checkout: canSalesBypass,
        discount_coupon: "",
        // *** The whole point of the non-healthcare flow: stamp the vertical
        // so the server-side facility row carries it, and the dashboard's
        // vertical-filtered dropdown immediately shows the new facility. ***
        vertical: preselectedVertical,
        ...(isAviation
          ? {
              aviation_targeting: mergedAviationTargeting,
              primary_profile_slug:
                normalizedAviationTargeting.primary_profile_slug,
              profile_targeting_json: mergedAviationTargeting,
              activity_domains: normalizedAviationTargeting.activity_domains,
              operation_contexts:
                normalizedAviationTargeting.operation_contexts,
              asset_types: normalizedAviationTargeting.asset_types,
              certificates_or_approvals:
                normalizedAviationTargeting.certificates_or_approvals,
              regulation_packs: normalizedAviationTargeting.regulation_packs,
              overlay_packs: normalizedAviationTargeting.overlay_packs,
              penalty_packs: normalizedAviationTargeting.penalty_packs,
            }
          : {}),
        ...(isFoodService
          ? {
              primary_profile_slug:
                foodServiceTargeting.primary_profile_slug ||
                chosen?.primary_profile_slug ||
                undefined,
              profile_targeting_json: {
                ...foodServiceTargeting,
                ...(resolverTargeting || {}),
              },
              regulation_packs: foodServiceTargeting.regulation_packs,
              overlay_packs: foodServiceTargeting.overlay_packs,
              penalty_packs: foodServiceTargeting.penalty_packs,
            }
          : {}),
        ...(isPharma
          ? {
              primary_profile_slug:
                chosen?.primary_profile_slug ||
                selectedTypeRow?.type_slug ||
                undefined,
              profile_targeting_json: {
                ...(resolverTargeting || {}),
                external_source: externalSource,
                external_record_id: externalRecordId,
                external_display_name: externalDisplayName,
                source_url: sourceUrl,
                certificate_or_location_id: certificateOrLocationId,
              },
            }
          : {}),
      },
      userId,
      canSalesBypass,
    };
  };

  const handleSelectExisting = async (chosen: VerticalFacilityResult) => {
    await createPreferredFacility(chosen);
  };

  const handleCreateAsNew = async () => {
    if (!requireAviationWizardComplete()) return;
    if (!form.name.trim()) {
      toast.error("Please enter a facility name.");
      return;
    }
    if (!form.state) {
      toast.error("Please select a state.");
      return;
    }
    await createPreferredFacility(undefined);
  };

  const handleSelectAviationSuggestion = (
    chosen: VerticalFacilityResult | string | null,
  ) => {
    if (!chosen || typeof chosen === "string") return;
    const name = getFacilityDisplayName(chosen);
    const addr = chosen.primary_address ?? null;
    setForm((prev) => ({
      ...prev,
      name,
      city: addr?.city || prev.city,
      state: addr?.state || prev.state,
    }));
    setAviationSuggestions([chosen]);
    setAviationSuggestionOpen(false);
    setSearchError(null);
    setSearchNotice("Selected an aviation facility match. Confirm it below or search again.");
    setResults([chosen]);
  };

  const createPreferredFacility = async (
    chosen: VerticalFacilityResult | undefined,
  ) => {
    let resolvedAviationPayload: Record<string, unknown> | null = null;
    if (isAviation) {
      const basePayload = buildAviationTargetingPayload(aviationTargeting);
      try {
        resolvedAviationPayload = {
          ...(await resolveAviationTargeting(basePayload)),
        };
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to resolve aviation regulation packs.",
        );
        return;
      }
    }

    const { facility_details, userId, canSalesBypass } = buildFacilityDetails(
      chosen,
      resolvedAviationPayload,
    );
    if (!userId) {
      toast.error("Please sign in to add a facility.");
      return;
    }
    setLoadingCreate(true);
    try {
      const response = await createUserPreferredFacility({
        user_id: Number(userId),
        facility_details,
        is_verified: canSalesBypass,
      });
      if (chosen) {
        toast.success(`Selected facility added to your ${verticalLabel} workspace.`);
      } else {
        toast.success(`Facility added to your ${verticalLabel} workspace.`);
      }
      onAfterCreate?.();
      const preferredFacilityId =
        response.data?.id ?? (response as any)?.data?.data?.id;
      try {
        sessionStorage.removeItem("filtered_selectedFacility");
        sessionStorage.removeItem("selected_plan");
        sessionStorage.setItem(
          "preferred_facility_for_pricing",
          JSON.stringify({
            id: preferredFacilityId,
            user_id: Number(userId),
            is_verified: response.data?.is_verified === true,
            is_active: response.data?.is_active ?? true,
            facility_name: facility_details.facility_name || "",
            facility_type: facility_details.facility_type || "",
            facility_type_id: facility_details.facility_type_id,
            vertical: facility_details.vertical,
            primary_profile_slug: facility_details.primary_profile_slug,
            profile_targeting_json: facility_details.profile_targeting_json,
            discount_coupon_name: null,
            discount_end_date: null,
            facility_details,
          }),
        );
      } catch {
        // Pricing can still read route state when storage is unavailable.
      }
      navigate("/dashboard/facilities", {
        state: {
          currentView: "pricing",
          fromPreferredFacilityId: preferredFacilityId,
          facility_type_id: facility_details.facility_type_id,
          pricingReloadToken: Date.now(),
        },
        replace: true,
      });
    } catch (e) {
      const message =
        e instanceof Error
          ? safeUnwrap(e.message) || e.message
          : "Failed to add facility.";
      toast.error(message);
    } finally {
      setLoadingCreate(false);
    }
  };

  const renderResultCard = (item: VerticalFacilityResult, idx: number) => {
    const name =
      item.site_name || item.organization_name || `Result ${idx + 1}`;
    const addr = item.primary_address;
    const cityState = [addr?.city, addr?.state].filter(Boolean).join(", ");
    const facilityType = isAviation
      ? ""
      : item.facility_type?.display_name || item.facility_type?.type_slug || "";
    const sourceLabel = item.source_label || item.source || "";
    const sourceDetail = [
      item.certificate_or_location_id
        ? `ID: ${item.certificate_or_location_id}`
        : null,
      item.authority_type,
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <ResultCard
        key={`${item.facility?.id ?? idx}-${name}`}
        onClick={() => handleSelectExisting(item)}
        role="button"
        aria-label={`Select facility ${name}`}
      >
        <Typography sx={{ fontWeight: 600 }}>{name}</Typography>
        {cityState && (
          <Typography variant="body2" sx={{ color: "#5d6d7e" }}>
            {cityState}
          </Typography>
        )}
        {facilityType && (
          <Typography variant="body2" sx={{ color: "#34495e" }}>
            {facilityType}
          </Typography>
        )}
        {sourceLabel && (
          <Typography variant="body2" sx={{ color: "#34495e" }}>
            Source: {sourceLabel}
            {sourceDetail ? ` (${sourceDetail})` : ""}
          </Typography>
        )}
        {isAviation && item.source_refreshed_at && (
          <Typography variant="caption" sx={{ color: "#5d6d7e" }}>
            Feed refreshed:{" "}
            {new Date(item.source_refreshed_at).toLocaleDateString()}
          </Typography>
        )}
      </ResultCard>
    );
  };

  const setNotSureTriageField = (
    key: "operate" | "hold_certificate" | "assets",
    value: string,
  ) => {
    setAviationTargeting((prev) => ({
      ...prev,
      not_sure_triage: { ...prev.not_sure_triage, [key]: value },
    }));
    setAviationWizardComplete(false);
  };

  const renderCheckboxGrid = (
    field: AviationToggleArrayKey,
    rows: readonly (readonly [string, string])[],
  ) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        gap: "8px",
      }}
    >
      {rows.map(([value, optionLabel]) => {
        const checked = aviationTargeting[field].includes(value);
        const blurb = getWizardOptionBlurb(value, optionLabel);
        return (
          <Box
            key={value}
            component="label"
            sx={{
              alignItems: "flex-start",
              border: checked ? "1px solid #0a829d" : "1px solid #d6e7ec",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              gap: "8px",
              margin: 0,
              padding: "10px",
              backgroundColor: checked ? "#ecfcff" : "#fff",
            }}
          >
            <Checkbox
              checked={checked}
              onChange={() => toggleAviationTargetingValue(field, value)}
              size="small"
              sx={{
                color: "#94a3b8",
                padding: 0,
                paddingTop: "2px",
                "&.Mui-checked": { color: "#0a829d" },
              }}
            />
            <WizardOptionWithBlurb title={optionLabel} summary={blurb.summary} detail={blurb.detail} />
          </Box>
        );
      })}
    </Box>
  );

  const refinedProfileChoices =
    aviationWizardConfig.refinedProfileOptionsByFamily[
      effectivePrimaryActivityFamilyForWizard(
        aviationTargeting.primary_activity_family,
        aviationTargeting.additional_activity_families,
      )
    ] ?? aviationWizardConfig.refinedProfileOptionsByFamily.not_sure;

  return (
    <ContentContainer>
      <Box>
        <Typography
          sx={{ fontSize: "22px", fontWeight: 700, color: "#0d3b66" }}
        >
          Add {/^[aeiouAEIOU]/.test(verticalLabel) ? "an" : "a"} {verticalLabel} facility
        </Typography>
        <Typography sx={{ color: "#5d6d7e", marginTop: "4px" }}>
          Search by name and location, or add a new facility manually.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {isAviation && (
          <Box
            sx={{
              border: "1px solid #99d4e1",
              borderRadius: "10px",
              padding: "18px",
              backgroundColor: "#f9fdff",
            }}
          >
            {aviationTaxonomyLoading && (
              <Typography sx={{ color: "#5d6d7e", mb: 1 }}>
                Loading aviation taxonomy…
              </Typography>
            )}
            {aviationTaxonomyError && (
              <Typography sx={{ color: "#b42318", mb: 1 }}>
                Aviation taxonomy failed to load: {aviationTaxonomyError}
              </Typography>
            )}
            <SectionTitle>Aviation Wizard</SectionTitle>
            <Typography sx={{ color: "#5d6d7e", marginTop: "4px" }}>
              Step {aviationWizardStep + 1} of {aviationWizardConfig.wizardSteps.length}:{" "}
              {currentAviationWizardStep.title}
            </Typography>
            <Box sx={{ display: "flex", gap: "6px", margin: "14px 0" }}>
              {aviationWizardConfig.wizardSteps.map((step, idx) => (
                <Box
                  key={step.id}
                  sx={{
                    flex: 1,
                    height: "6px",
                    borderRadius: "999px",
                    backgroundColor:
                      idx <= aviationWizardStep ? "#0a829d" : "#d6e7ec",
                  }}
                />
              ))}
            </Box>
            <Typography sx={{ fontWeight: 600, color: "#0d3b66" }}>
              {currentAviationWizardStep.title}
            </Typography>
            <Typography
              sx={{
                color: "#5d6d7e",
                marginTop: "4px",
                marginBottom: "12px",
                whiteSpace: "pre-line",
                lineHeight: 1.55,
                fontSize: currentAviationWizardStep.id === "authority_profile" ? "14px" : undefined,
              }}
            >
              {currentAviationWizardStep.id === "authority_profile"
                ? getAuthorityProfileStepDescription(
                    aviationTargeting.primary_activity_family,
                    aviationTargeting.additional_activity_families,
                  )
                : currentAviationWizardStep.description}
            </Typography>

            {currentAviationWizardStep.id === "activity_family" && (
              <>
                <Typography sx={{ color: "#5d6d7e", marginBottom: "12px", fontSize: "13px" }}>
                  Choose the main regulated activity. You can add additional functions below.
                </Typography>
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                  <Select
                    displayEmpty
                    value={aviationTargeting.primary_activity_family}
                    onChange={(e) =>
                      setPrimaryActivityFamily(e.target.value as PrimaryActivityFamily | "")
                    }
                    inputProps={{ "aria-label": "Primary aviation activity family" }}
                  >
                    <MenuItem value="">
                      <em>Primary activity *</em>
                    </MenuItem>
                    {aviationWizardConfig.primaryActivityFamilyOptions.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{ fontWeight: 600, color: "#0d3b66", mb: 1 }}>
                  Additional regulated functions (optional)
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: "8px",
                  }}
                >
                  {aviationWizardConfig.primaryActivityFamilyOptions.filter(
                    (o) =>
                      o.value !== aviationTargeting.primary_activity_family &&
                      o.value !== "not_sure",
                  ).map(({ value, label }) => {
                    const checked = aviationTargeting.additional_activity_families.includes(value);
                    return (
                      <Box
                        key={value}
                        component="label"
                        sx={{
                          alignItems: "flex-start",
                          border: checked ? "1px solid #0a829d" : "1px solid #d6e7ec",
                          borderRadius: "8px",
                          cursor: "pointer",
                          display: "flex",
                          gap: "8px",
                          padding: "10px",
                          backgroundColor: checked ? "#ecfcff" : "#fff",
                          margin: 0,
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleAdditionalActivityFamily(value)}
                          size="small"
                          sx={{
                            color: "#94a3b8",
                            padding: 0,
                            paddingTop: "2px",
                            "&.Mui-checked": { color: "#0a829d" },
                          }}
                        />
                        <ActivityFamilyLabelWithInfo
                          family={value}
                          label={label}
                        />
                      </Box>
                    );
                  })}
                </Box>
                {aviationTargeting.primary_activity_family === "not_sure" && (
                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                      Three plain-English questions (optional)
                    </Typography>
                    <TextField
                      size="small"
                      label="Operate aircraft, airport, maintenance, manufacturing, training, sales, or security/hazmat?"
                      value={aviationTargeting.not_sure_triage?.operate ?? ""}
                      onChange={(e) => setNotSureTriageField("operate", e.target.value)}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Hold FAA, TSA, DOT, or PHMSA certificate / approval / OpSpec / LOA?"
                      value={aviationTargeting.not_sure_triage?.hold_certificate ?? ""}
                      onChange={(e) => setNotSureTriageField("hold_certificate", e.target.value)}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Assets: aircraft, airport, runway, hangar, plant, UAS, cargo, security area, launch site?"
                      value={aviationTargeting.not_sure_triage?.assets ?? ""}
                      onChange={(e) => setNotSureTriageField("assets", e.target.value)}
                      fullWidth
                    />
                  </Box>
                )}
              </>
            )}

            {currentAviationWizardStep.id === "authority_profile" && (
              <FormControl component="fieldset" fullWidth variant="standard">
                <Typography component="legend" sx={{ fontSize: "13px", fontWeight: 600, color: "#0d3b66", mb: 1.5 }}>
                  Choose all regulatory identities that apply *
                </Typography>
                <Box role="group" aria-label="Regulatory profiles">
                  {refinedProfileChoices.map(([slug, label]) => {
                    const blurb = getWizardOptionBlurb(slug, label);
                    const selected = selectedProfileSlugs(aviationTargeting).includes(slug);
                    return (
                      <FormControlLabel
                        key={slug}
                        control={
                          <Checkbox
                            checked={selected}
                            onChange={() => toggleAviationProfileSlug(slug)}
                            size="small"
                            sx={{
                              color: "#94a3b8",
                              padding: "6px",
                              "&.Mui-checked": { color: "#0a829d" },
                            }}
                          />
                        }
                        label={
                          <WizardOptionWithBlurb
                            title={label}
                            summary={blurb.summary}
                            detail={blurb.detail}
                          />
                        }
                        sx={{
                          alignItems: "flex-start",
                          margin: 0,
                          marginBottom: "10px",
                          padding: "10px 12px",
                          border: selected ? "2px solid #0a829d" : "1px solid #d6e7ec",
                          borderRadius: "10px",
                          width: "100%",
                          maxWidth: "100%",
                          boxSizing: "border-box",
                          backgroundColor: selected ? "#ecfcff" : "#ffffff",
                          cursor: "pointer",
                        }}
                      />
                    );
                  })}
                </Box>
                <FormHelperText sx={{ marginLeft: 0, marginTop: "4px" }}>
                  Select every certificate path that fits — hover any row for full CFR-style detail.
                </FormHelperText>
              </FormControl>
            )}

            {currentAviationWizardStep.id === "scope_assets" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography sx={{ fontWeight: 600, color: "#0d3b66" }}>Operating contexts</Typography>
                {renderCheckboxGrid("operation_contexts", aviationWizardConfig.scopeMultiOptions.operation_contexts)}
                <Typography sx={{ fontWeight: 600, color: "#0d3b66" }}>Assets</Typography>
                {renderCheckboxGrid("asset_types", aviationWizardConfig.scopeMultiOptions.asset_types)}
                <Typography sx={{ fontWeight: 600, color: "#0d3b66" }}>
                  Certificates / approvals
                </Typography>
                {renderCheckboxGrid(
                  "certificates_or_approvals",
                  aviationWizardConfig.scopeMultiOptions.certificates_or_approvals,
                )}
                <Typography sx={{ fontWeight: 600, color: "#0d3b66", marginTop: 1 }}>
                  What should we watch for first?
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#5d6d7e", mb: 0.5 }}>
                  Defaults reflect your profile; adjust as you like. Advisory Circulars are not
                  treated as mandatory unless you adopt them or they are in an approved program.
                </Typography>
                {renderCheckboxGrid("risk_targets", aviationWizardConfig.riskTargetOptions)}
              </Box>
            )}

            {currentAviationWizardStep.id === "facility_location" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box>
                  <Box sx={{ mb: 0.75 }}>
                    <WizardOptionWithBlurb
                      title="Facility or entity name"
                      summary={AVIATION_LOCATION_FIELD_BLURBS.facility_name.summary}
                      detail={AVIATION_LOCATION_FIELD_BLURBS.facility_name.detail}
                    />
                  </Box>
                  <Autocomplete<VerticalFacilityResult, false, false, true>
                    freeSolo
                    open={aviationSuggestionOpen && form.name.trim().length > 0}
                    onOpen={() => setAviationSuggestionOpen(true)}
                    onClose={() => setAviationSuggestionOpen(false)}
                    inputValue={form.name}
                    options={aviationSuggestions}
                    ListboxProps={{ ref: aviationSuggestionListRef }}
                    loading={loadingAviationSuggestions}
                    filterOptions={(options) =>
                      sortAviationSuggestionsForInput(
                        options.filter((option) =>
                          aviationSuggestionMatchesInput(option, form.name),
                        ),
                        form.name,
                      )
                    }
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : getFacilityDisplayName(option)
                    }
                    isOptionEqualToValue={(option, value) =>
                      getAviationSuggestionKey(option) === getAviationSuggestionKey(value)
                    }
                    onInputChange={(_, value) => handleAviationNameInput(value)}
                    onChange={(_, value) => handleSelectAviationSuggestion(value)}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
                        key: React.Key;
                      };
                      const location = getFacilityCityState(option);
                      const source = option.source_label || option.source || "";
                      const id = option.certificate_or_location_id || option.external_id || "";
                      return (
                        <Box key={key} component="li" {...optionProps}>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography sx={{ fontWeight: 600 }}>
                              {getAviationSuggestionTitle(option)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#5d6d7e" }}>
                              {[location, source, id ? `ID: ${id}` : ""]
                                .filter(Boolean)
                                .join(" · ")}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                    noOptionsText={
                      loadingAviationSuggestions
                        ? "Searching aviation facilities..."
                        : "No aviation matches"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Facility or entity name *"
                        size="small"
                        inputProps={{
                          ...params.inputProps,
                          "aria-label": "Facility or entity name",
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingAviationSuggestions ? (
                                <CircularProgress color="inherit" size={16} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <FormHelperText sx={{ marginLeft: 0, marginTop: "8px", color: "#5d6d7e" }}>
                    Start typing in the name field — we search FAA and partner feeds as you type (about
                    250ms after you pause). State and city narrow duplicates; the full match list also
                    appears after you tap Continue to Facility Search below.
                  </FormHelperText>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Box sx={{ mb: 0.75 }}>
                      <WizardOptionWithBlurb
                        title="State"
                        summary={AVIATION_LOCATION_FIELD_BLURBS.state.summary}
                        detail={AVIATION_LOCATION_FIELD_BLURBS.state.detail}
                      />
                    </Box>
                    <FormControl size="small" fullWidth>
                      <Select
                        displayEmpty
                        value={form.state}
                        onChange={handleStateChange}
                        inputProps={{ "aria-label": "Regulated entity state" }}
                      >
                        <MenuItem value="">
                          <em>State (optional)</em>
                        </MenuItem>
                        {states.map((s) => (
                          <MenuItem
                            key={s.id ?? s.state_abbreviation}
                            value={s.state_abbreviation}
                          >
                            {s.name} ({s.state_abbreviation})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Box sx={{ mb: 0.75 }}>
                      <WizardOptionWithBlurb
                        title="City"
                        summary={AVIATION_LOCATION_FIELD_BLURBS.city.summary}
                        detail={AVIATION_LOCATION_FIELD_BLURBS.city.detail}
                      />
                    </Box>
                    <TextField
                      label="City"
                      value={form.city}
                      onChange={handleField("city")}
                      size="small"
                      fullWidth
                      inputProps={{ "aria-label": "Regulated entity city" }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: "10px", marginTop: "16px" }}>
              <Button
                variant="outlined"
                onClick={handleAviationWizardBack}
                disabled={
                  aviationWizardStep === 0 ||
                  aviationTaxonomyLoading ||
                  Boolean(aviationTaxonomyError)
                }
                sx={{ textTransform: "none" }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleAviationWizardNext}
                disabled={aviationTaxonomyLoading || Boolean(aviationTaxonomyError)}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#0d3b66",
                  ":hover": { backgroundColor: "#0b2f50" },
                }}
              >
                {isFinalAviationWizardStep
                  ? "Continue to Facility Search"
                  : "Next"}
              </Button>
              {aviationWizardComplete && (
                <Button
                  variant="text"
                  onClick={handleAviationWizardEdit}
                  sx={{ textTransform: "none" }}
                >
                  Edit Answers
                </Button>
              )}
            </Box>
          </Box>
        )}
        {isFoodService && (
          <Box
            sx={{
              border: "1px solid #99d4e1",
              borderRadius: "10px",
              padding: "18px",
              backgroundColor: "#f9fdff",
            }}
          >
            <SectionTitle>Food Service Profile</SectionTitle>
            <Typography sx={{ color: "#5d6d7e", marginTop: "4px", marginBottom: "12px" }}>
              Choose the regulated food service type and any special contexts so regulations and penalties are targeted correctly.
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                displayEmpty
                value={foodServiceTargeting.food_service_facility_subtype}
                onChange={(e) => setFoodServiceSubtype(e.target.value)}
                inputProps={{ "aria-label": "Food Service facility type" }}
                renderValue={(selected) => {
                  const selectedType = facilityTypes.find(
                    (type) => type.type_slug === selected,
                  );
                  return selectedType?.display_name || "Food Service facility type *";
                }}
              >
                <MenuItem value="">
                  <em>Food Service facility type *</em>
                </MenuItem>
                {facilityTypes
                  .filter((type) => Boolean(type.type_slug))
                  .map((type) => (
                    <MenuItem key={type.type_slug} value={type.type_slug || ""}>
                      {type.display_name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Typography sx={{ fontWeight: 600, color: "#0d3b66", marginTop: "16px" }}>
              Operational Contexts
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: "8px", marginTop: "8px" }}>
              {FOOD_SERVICE_CONTEXT_OPTIONS.map(([value, label]) => (
                <Box key={value} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Checkbox
                    size="small"
                    checked={foodServiceTargeting.food_service_contexts.includes(value)}
                    onChange={() => toggleFoodServiceTargetingValue("food_service_contexts", value)}
                  />
                  <Typography sx={{ fontSize: "14px" }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            <Typography sx={{ fontWeight: 600, color: "#0d3b66", marginTop: "16px" }}>
              Special Processes
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: "8px", marginTop: "8px" }}>
              {FOOD_SERVICE_SPECIAL_PROCESS_OPTIONS.map(([value, label]) => (
                <Box key={value} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Checkbox
                    size="small"
                    checked={foodServiceTargeting.food_service_special_processes.includes(value)}
                    onChange={() => toggleFoodServiceTargetingValue("food_service_special_processes", value)}
                  />
                  <Typography sx={{ fontSize: "14px" }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {(!isAviation || aviationWizardComplete) && (
          <>
        <SectionTitle>Search</SectionTitle>
        <FormRow>
          {isAviation ? (
            <Autocomplete<VerticalFacilityResult, false, false, true>
              freeSolo
              open={aviationSuggestionOpen && form.name.trim().length > 0}
              onOpen={() => setAviationSuggestionOpen(true)}
              onClose={() => setAviationSuggestionOpen(false)}
              inputValue={form.name}
              options={aviationSuggestions}
              ListboxProps={{ ref: aviationSuggestionListRef }}
              loading={loadingAviationSuggestions}
              filterOptions={(options) =>
                sortAviationSuggestionsForInput(
                  options.filter((option) =>
                    aviationSuggestionMatchesInput(option, form.name),
                  ),
                  form.name,
                )
              }
              getOptionLabel={(option) =>
                typeof option === "string" ? option : getFacilityDisplayName(option)
              }
              isOptionEqualToValue={(option, value) =>
                getAviationSuggestionKey(option) === getAviationSuggestionKey(value)
              }
              onInputChange={(_, value) => handleAviationNameInput(value)}
              onChange={(_, value) => handleSelectAviationSuggestion(value)}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
                  key: React.Key;
                };
                const location = getFacilityCityState(option);
                const source = option.source_label || option.source || "";
                const id = option.certificate_or_location_id || option.external_id || "";
                return (
                  <Box key={key} component="li" {...optionProps}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {getAviationSuggestionTitle(option)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#5d6d7e" }}>
                        {[location, source, id ? `ID: ${id}` : ""]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              noOptionsText={
                loadingAviationSuggestions
                  ? "Searching aviation facilities..."
                  : "No aviation matches"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Facility name *"
                  size="small"
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": "Facility name",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAviationSuggestions ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            <TextField
              label="Facility name *"
              value={form.name}
              onChange={handleField("name")}
              size="small"
              inputProps={{ "aria-label": "Facility name" }}
            />
          )}
          <FormControl size="small">
            <Select
              displayEmpty
              value={form.state}
              onChange={handleStateChange}
              inputProps={{ "aria-label": "Facility search state" }}
            >
              <MenuItem value="">
                <em>State (optional)</em>
              </MenuItem>
              {states.map((s) => (
                <MenuItem
                  key={s.id ?? s.state_abbreviation}
                  value={s.state_abbreviation}
                >
                  {s.name} ({s.state_abbreviation})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="City"
            value={form.city}
            onChange={handleField("city")}
            size="small"
            inputProps={{ "aria-label": "Facility search city" }}
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={handleField("phone")}
            size="small"
            inputProps={{ "aria-label": "Phone" }}
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={handleField("email")}
            size="small"
            inputProps={{ "aria-label": "Email" }}
          />
          {!isAviation && !isFoodService && (
            <FormControl size="small">
              <Select
                displayEmpty
                value={form.facilityTypeId}
                onChange={handleFacilityTypeChange}
                inputProps={{ "aria-label": "Facility type" }}
              >
                <MenuItem value="">
                  <em>Facility type (optional)</em>
                </MenuItem>
                {facilityTypes.map((ft) => (
                  <MenuItem key={ft.id} value={ft.id}>
                    {ft.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </FormRow>
        <SearchButton
          onClick={handleSearch}
          disabled={loadingSearch}
          startIcon={
            loadingSearch ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <SearchIcon />
            )
          }
        >
          {loadingSearch ? "Searching..." : "Search"}
        </SearchButton>
          </>
        )}
      </Box>

      {searchError && (
        <Typography sx={{ color: "#c0392b" }} role="alert">
          {searchError}
        </Typography>
      )}
      {searchNotice && (
        <Typography sx={{ color: "#5d6d7e" }}>
          {searchNotice}
        </Typography>
      )}

      {results !== null && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <SectionTitle>
            {results.length > 0
              ? `Existing ${verticalLabel} facilities (${results.length})`
              : "No matches found"}
          </SectionTitle>
          {results.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "12px",
              }}
            >
              {results.map(renderResultCard)}
            </Box>
          ) : (
            <NoResultsBanner>
              {searchBackend === "fda_establishment_resolvers" ||
              searchBackend === "aviation_source_resolvers" ||
              searchBackend === "food_service_source_resolvers" ? (
                <>
                  We could not find an existing {verticalLabel} facility for
                  &ldquo;{form.name.trim()}&rdquo; in public{" "}
                  {verticalLabel.toLowerCase()} directories. You can add this
                  facility as a new entry below — it will be saved to your
                  workspace and available to switch to immediately.
                </>
              ) : (
                <>
                  We could not find an existing {verticalLabel} facility for
                  &ldquo;{form.name.trim()}&rdquo; in the local Unicomply
                  facility table. You can add this facility as a new entry
                  below — it will be saved to your workspace and available to
                  switch to immediately.
                </>
              )}
            </NoResultsBanner>
          )}
          <Button
            variant="outlined"
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
            onClick={handleCreateAsNew}
            disabled={loadingCreate}
          >
            {loadingCreate
              ? "Adding..."
              : `Add "${form.name.trim() || "this facility"}" as new`}
          </Button>
        </Box>
      )}
    </ContentContainer>
  );
}

function getFacilityDisplayName(item: VerticalFacilityResult): string {
  return item.site_name || item.organization_name || "Aviation facility";
}

function getAviationFacilityCode(item: VerticalFacilityResult): string {
  return item.certificate_or_location_id || item.external_id || "";
}

function getAviationSuggestionTitle(item: VerticalFacilityResult): string {
  const code = getAviationFacilityCode(item);
  const name = getFacilityDisplayName(item);
  return code ? `${code} - ${name}` : name;
}

function getFacilityCityState(item: VerticalFacilityResult): string {
  const addr = item.primary_address ?? null;
  return [addr?.city, addr?.state].filter(Boolean).join(", ");
}

function getAviationSuggestionKey(item: VerticalFacilityResult): string {
  const code = getAviationFacilityCode(item);
  if (code) {
    return [item.source, code].filter(Boolean).join("|");
  }
  return [
    item.source,
    item.external_id,
    item.certificate_or_location_id,
    getFacilityDisplayName(item),
    getFacilityCityState(item),
  ]
    .filter(Boolean)
    .join("|");
}

function normalizeFacilitySearchText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function aviationSuggestionMatchesInput(
  item: VerticalFacilityResult,
  inputValue: string,
): boolean {
  const needle = normalizeFacilitySearchText(inputValue);
  if (!needle) return true;

  const name = normalizeFacilitySearchText(getFacilityDisplayName(item));
  const code = normalizeFacilitySearchText(getAviationFacilityCode(item));
  if (code.startsWith(needle)) return true;
  if (name.startsWith(needle)) return true;

  // Require at least 3 characters before matching city / ID tails (reduces noise for "a", "al").
  if (needle.length < 3) return false;

  const addr = item.primary_address ?? null;
  return [
    addr?.city,
    item.certificate_or_location_id,
    item.external_id,
  ].some((value) => normalizeFacilitySearchText(value || "").startsWith(needle));
}

function mergeAviationSuggestions(
  inputValue: string,
  apiResults: VerticalFacilityResult[],
): VerticalFacilityResult[] {
  return sortAviationSuggestionsForInput(apiResults, inputValue);
}

function sortAviationSuggestionsForInput(
  options: VerticalFacilityResult[],
  inputValue: string,
): VerticalFacilityResult[] {
  const needle = normalizeFacilitySearchText(inputValue);
  return [...options].sort((left, right) => {
    const leftRank = getAviationSuggestionRank(left, needle);
    const rightRank = getAviationSuggestionRank(right, needle);
    if (leftRank !== rightRank) return leftRank - rightRank;
    return getAviationSuggestionTitle(left).localeCompare(getAviationSuggestionTitle(right));
  });
}

function getAviationSuggestionRank(item: VerticalFacilityResult, needle: string): number {
  const code = normalizeFacilitySearchText(getAviationFacilityCode(item));
  const name = normalizeFacilitySearchText(getFacilityDisplayName(item));
  const location = normalizeFacilitySearchText(getFacilityCityState(item));
  if (!needle) return 4;
  if (code === needle) return 0;
  if (code.startsWith(needle)) return 1;
  if (name.startsWith(needle)) return 2;
  if (location.startsWith(needle)) return 3;
  return 4;
}

/** Some payment.ts errors are JSON-stringified `{message,success}` blobs. */
function safeUnwrap(message: string): string | undefined {
  try {
    const parsed = JSON.parse(message);
    if (parsed && typeof parsed.message === "string") return parsed.message;
  } catch {
    // not JSON
  }
  return undefined;
}
